"use client";

import Link from "next/link";
import { trackBuyClick, trackCTA } from "@/lib/analytics";
import { PLAN_LIMITS } from "@/lib/constants";

const plans = [
  {
    name: "Starter",
    price: "$0",
    priceNum: 0,
    period: "/mo",
    features: [
      "Unlimited study sessions",
      `${PLAN_LIMITS.free.pdfsPerMonth} AI generations / month`,
      "PDFs, docs, YouTube & URLs",
      `${PLAN_LIMITS.free.maxDecks} decks`,
      "CSV backup",
    ],
    cta: "Start Studying Free",
    highlight: false,
    bg: "bg-warm-white",
  },
  {
    name: "Pro Student",
    price: "$7",
    priceNum: 7,
    period: "/mo",
    annualPrice: "$48/yr",
    features: [
      "Unlimited AI generation",
      "Unlimited decks & bigger sessions",
      "Scanned PDF support",
      "First access to new AI study tools",
      "Anki .apkg backup (insurance)",
    ],
    cta: "Go Pro",
    highlight: true,
    bg: "bg-primary",
    badge: "BEST VALUE",
  },
];

export function Pricing() {
  return (
    <section className="w-full border-y-2 border-border-thick bg-white py-24 px-6" id="pricing">
      <h2 className="text-4xl font-black font-display text-center mb-4 max-w-7xl mx-auto">Simple Pricing for Students</h2>
      <p className="text-center text-border-thick/60 max-w-xl mx-auto mb-16 text-lg font-medium">
        Free plan lets you study unlimited every day. Pro unlocks more AI and deeper study tools.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start max-w-3xl mx-auto">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`${plan.bg} border-2 border-border-thick rounded-xl p-8 ${plan.highlight ? "shadow-brutal relative transform md:-translate-y-4 z-10" : "shadow-brutal"}`}
          >
            {plan.badge && (
              <div className="absolute top-0 right-0 bg-safety-orange text-white text-xs font-bold font-display px-3 py-1 border-l-2 border-b-2 border-border-thick rounded-bl-lg">
                {plan.badge}
              </div>
            )}
            <h3 className={`text-2xl font-bold font-display mb-2 ${plan.highlight ? "text-white" : ""}`}>{plan.name}</h3>
            <div className={`flex items-baseline gap-1 mb-2 ${plan.highlight ? "text-white" : ""}`}>
              <span className="text-4xl font-bold font-display">{plan.price}</span>
              <span className={`font-bold ${plan.highlight ? "text-white/80" : "text-border-thick/50"}`}>{plan.period}</span>
            </div>
            {"annualPrice" in plan && plan.annualPrice && (
              <p className={`text-sm font-semibold mb-4 ${plan.highlight ? "text-white/70" : "text-border-thick/40"}`}>
                or {plan.annualPrice} — save 43%
              </p>
            )}
            <ul className={`space-y-4 mb-8 ${plan.highlight ? "text-white" : ""}`}>
              {plan.features.map((f) => (
                <li key={f} className="flex items-center gap-3">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={plan.highlight ? "" : "text-success"}><polyline points="20 6 9 17 4 12"/></svg>
                  <span className={plan.highlight ? "font-bold" : "font-medium"}>{f}</span>
                </li>
              ))}
            </ul>
            <Link
              href="/login"
              onClick={() => {
                if (plan.priceNum > 0) {
                  trackBuyClick(plan.name.toLowerCase(), plan.priceNum);
                } else {
                  trackCTA("pricing-starter");
                }
              }}
              className={`block w-full py-3 text-center border-2 border-border-thick rounded-lg font-bold font-display transition-all ${
                plan.highlight
                  ? "bg-white text-primary shadow-brutal-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px]"
                  : "hover:bg-retro-gray/30"
              }`}
            >
              {plan.cta}
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}
